import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import { makeStyles } from '@material-ui/styles';
import MenuIcon from '@material-ui/icons/Menu'
import {
  AppBar,
  IconButton,
  Toolbar,
  colors,
  Typography,
  Theme
} from '@material-ui/core';
import InputIcon from '@material-ui/icons/Input';
import AccessibilityNewIcon from '@material-ui/icons/AccessibilityNew';

import { AccessibilityPopover, DeleteModal } from 'components';
import Auth from 'services/auth';
import useTextarea from 'utils/useTextarea';
import { useSessionSelector } from 'reducers/sessionReducer';
import authenticate from 'store/authenticate';
import env from 'env';
import HiddenWithChildren from 'components/HiddenWithChildren';
import useRouter from 'utils/useRouter';


const useStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: 'none'
  },
  flexGrow: {
    flexGrow: 1
  },
  accessibilityButton: {
    marginLeft: theme.spacing(1)
  },
  accessibilityBadge: {
    backgroundColor: colors.orange[600]
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    color: 'white',
    '& > img': {
      marginRight: theme.spacing(1)
    }
  },
  logoutButton: {
    marginLeft: theme.spacing(1)
  },
  logoutIcon: {
    marginRight: theme.spacing(1)
  }
}));

const TopBar = props => {
  const { onOpenNavBarMobile, className, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSessionSelector();
  const router = useRouter();
  const accessibilityRef = useRef(null);
  const [openPrompt, setOpenPrompt] = useState(false);
  const [openAccessibility, setOpenAccessibility] = useState(false);

  const textarea = useTextarea({
    title: 'Enter target userID',
    InputProps: {
      type: 'number',
      placeholder: 'UserID',
      helperText: 'This tool allows you to log into any user. The user you want to log in as will be remembered in localstorage. This is only enabled in the dev environment. By default the QA user is used (uid 901)'
    },
    validate: (val) => parseInt(val) ? null:'Please enter a number'
  });

  useEffect(() => {
    window.onbeforeunload = (event) => {
      event.preventDefault();
      return event.returnValue = 'Are you sure you want to exit?';
    };
  }, [])

  const items = [
    {
      title: 'Toggle High contrast',
      onClick: () => {
        setOpenAccessibility(false);
        document.body.classList.toggle('highContrast');
      }
    }
  ];

  const handleLogout = () => {
    Auth.logout(user.loginID)
      .then(() => {
        window.onbeforeunload = null;
        router.history.push('/auth/logged-out');
      })
  };

  const handleAccessibilityOpen = () => {
    setOpenAccessibility(true);
  };

  const handleAccessibilityClose = () => {
    setOpenAccessibility(false);
  };

  const handleClosePrompt = () => {
    setOpenPrompt(false)
  }

  const handleDevLogin = async () => {
    if(env.env !== 'dev') {
      return;
    }
    const id = parseInt(await textarea());
    localStorage.setItem('userID', id.toString());
    await Auth.logout();
    await dispatch(authenticate);
  }

  return (
    <AppBar
      {...rest}
      className={clsx(classes.root, className)}
      color="primary"
    >
      <DeleteModal
        button="Logout"
        buttonIcon={<InputIcon />}
        content={<div>You are about to leave the Rguroo Dashboard. You will lose any unsaved data! Continue with logout?</div>}
        onClose={handleClosePrompt}
        onDelete={handleLogout}
        open={openPrompt}
        title="Logout"
      />
      <Toolbar>
        <RouterLink
          className={classes.logo}
          to="/"
        >
          <img
            alt="Logo"
            src="/images/logos/logo.svg"
          />
          <Typography
            color="inherit"
            variant="h4"
          >
            Dashboard
          </Typography>
        </RouterLink>
        <div className={classes.flexGrow} />
        <Typography
          color="inherit"
          variant="body1"
        >
          Welcome {user.firstName}
        </Typography>
        <IconButton
          className={classes.accessibilityButton}
          color="inherit"
          onClick={handleAccessibilityOpen}
          ref={accessibilityRef}
        >
          <AccessibilityNewIcon />
        </IconButton>
        {env.env === 'dev' && <IconButton
          color='inherit'
          onClick={handleDevLogin}
        >
          <DeveloperBoardIcon/>
        </IconButton>}
        <IconButton
          color="inherit"
          onClick={() => setOpenPrompt(true)}
        >
          <InputIcon />
        </IconButton>
        <HiddenWithChildren lgUp>
          <IconButton
            color="inherit"
            onClick={onOpenNavBarMobile}
          >
            <MenuIcon />
          </IconButton>
        </HiddenWithChildren> 
      </Toolbar>
      <AccessibilityPopover
        anchorEl={accessibilityRef.current}
        items={items}
        onClose={handleAccessibilityClose}
        open={openAccessibility}
      />
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onOpenNavBarMobile: PropTypes.func
};

export default TopBar;
